import { makeStyles } from "tss-react/mui"
import { MenuItem, TextField } from "@mui/material"
import { CreateRoomResourceForm, IntegrationType, RoomDetailsColor, WalkDirection } from "../../../resourcesTypes"
import { StandardForm } from "../../../../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../../../../shared/components/forms/StandardMainPanel"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import {
    FormikAutoComplete,
    FormikDropdown,
    FormikSwitch,
    FormikTextField,
} from "../../../../../../../../shared/components/forms/Formik"
import { StandardSidePanel } from "../../../../../../../../shared/components/forms/StandardSidePanel"

import { FormikImageUpload } from "../../../../../../../../shared/components/Formik/FormikImageUpload"
import { InfoBox } from "../../../../../../../profile/components/InfoBox"
import { useFormikContext } from "formik"

import { AVEquipmentSelector } from "./AVEquipmentSelector"
import { NotificationsTable } from "./NotificationsTable"
import { StandardAlertInfo } from "../../../../../../../../shared/components/alert/StandardAlertInfo"
import { iterateStringEnum } from "../../../../../../../../lib/enums"
import { useMemo } from "react"
import { TranslationKey } from "../../../../../../../localization/translations"

const useStyles = makeStyles()((theme) => ({
    roomNumberInput: {
        width: "50%",
    },
    percentageInput: {
        width: "7.5rem",
    },
}))

const cleanupBufferOptions = Array.from({ length: 12 }, (_, index) => index * 5)

export function FormGeneralContent() {
    const { classes } = useStyles()
    const { values } = useFormikContext<CreateRoomResourceForm>()

    const integrationInfo = useMemo((): {
        id: TranslationKey
        value: string
    } => {
        if (values.integrationType === IntegrationType.MEETING_INVITE) {
            return {
                id: "resources-create-room-tab-general-side-integrations-meeting-invite",
                value: "Used for integrating with Exchange or other calendar systems. The email address will be added to the meeting invitation by FacilityNet Outlook Add-in when booking the room.",
            }
        }
        if (values.integrationType === IntegrationType.STARLEAF) {
            return {
                id: "resources-create-room-tab-general-side-integrations-starleaf",
                value: "Used for integrating with StarLeaf equipment that come with a dedicated email address used for integration.",
            }
        }
        return {
            id: "resources-create-room-tab-general-side-integrations-ews",
            value: "Used for integrating with equipment that reads from a calendar. The calendar of the mailbox will be booked using an EWS service account when booking the room.",
        }
    }, [values.integrationType])

    return (
        <StandardForm>
            <StandardMainPanel>
                <LocalizedStrict id="resources-create-room-tab-general-room-number-label" attrs={{ label: true }}>
                    <FormikTextField className={classes.roomNumberInput} name="reference" label="Room number" />
                </LocalizedStrict>
                <LocalizedStrict id="resources-create-room-tab-general-name-label" attrs={{ label: true }}>
                    <FormikTextField name="name" label="Name" fullWidth />
                </LocalizedStrict>
                <LocalizedStrict id="resources-create-room-tab-general-description-label" attrs={{ label: true }}>
                    <FormikTextField rows={4} multiline name="description" label="Description" fullWidth />
                </LocalizedStrict>
                <LocalizedStrict id="merchant-menu-planning-dialog-upload-image" attrs={{ label: true }}>
                    <FormikImageUpload label="Upload image" name="image.uid" typeOfEntity="uid" />
                </LocalizedStrict>
            </StandardMainPanel>

            <LocalizedStrict id="resources-create-room-tab-general-side-settings" attrs={{ title: true }}>
                <StandardSidePanel variant="collapsible" title="Settings">
                    <LocalizedStrict id="resources-create-room-tab-general-active-label">
                        <FormikSwitch<CreateRoomResourceForm> name="isActive">Active:</FormikSwitch>
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="resources-create-room-tab-general-cleanup-buffer-label"
                        attrs={{ label: true }}
                    >
                        <FormikDropdown name="buffer" label="Cleanup buffer">
                            {cleanupBufferOptions.map((value) => (
                                <MenuItem key={value} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>
                    <LocalizedStrict id="resources-create-room-tab-general-other-location-label">
                        <FormikSwitch name="roomDetails.isPrivateOffice">Other location:</FormikSwitch>
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="resources-create-room-tab-general-other-location-description"
                        attrs={{ title: true }}
                    >
                        <StandardAlertInfo
                            title={`Marking a room as "Other location" will allow the user to type an ad-hoc location to hold the meeting such as their office.`}
                        />
                    </LocalizedStrict>
                </StandardSidePanel>
            </LocalizedStrict>

            <LocalizedStrict id="resources-create-room-tab-general-meeting-display" attrs={{ title: true }}>
                <StandardSidePanel variant="collapsible" title="Settings">
                    <LocalizedStrict
                        id="resources-create-room-tab-general-walk-direction-label"
                        attrs={{ label: true }}
                    >
                        <FormikDropdown fullWidth name="roomDetails.walkDirection" label="Walk direction">
                            <MenuItem value={WalkDirection.NORTH}>
                                <LocalizedStrict id="resources-create-room-tab-general-walk-direction-north">
                                    <span>North</span>
                                </LocalizedStrict>
                            </MenuItem>
                            <MenuItem value={WalkDirection.EAST}>
                                <LocalizedStrict id="resources-create-room-tab-general-walk-direction-east">
                                    <span>East</span>
                                </LocalizedStrict>
                            </MenuItem>
                            <MenuItem value={WalkDirection.WEST}>
                                <LocalizedStrict id="resources-create-room-tab-general-walk-direction-west">
                                    <span>West</span>
                                </LocalizedStrict>
                            </MenuItem>
                            <MenuItem value={WalkDirection.SOUTH}>
                                <LocalizedStrict id="resources-create-room-tab-general-walk-direction-south">
                                    <span>South</span>
                                </LocalizedStrict>
                            </MenuItem>
                        </FormikDropdown>
                    </LocalizedStrict>
                </StandardSidePanel>
            </LocalizedStrict>

            <LocalizedStrict id="resources-create-room-tab-general-side-filter-details" attrs={{ title: true }}>
                <StandardMainPanel title="Filter details">
                    <LocalizedStrict id="resources-create-room-tab-general-seats-label" attrs={{ label: true }}>
                        <FormikTextField
                            className={classes.roomNumberInput}
                            name="roomDetails.sittings"
                            label="Seats"
                        />
                    </LocalizedStrict>
                    <LocalizedStrict id="resources-create-room-tab-general-floor-label" attrs={{ label: true }}>
                        <FormikTextField className={classes.roomNumberInput} name="roomDetails.floor" label="Floor" />
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="resources-create-room-tab-general-area-label"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <FormikTextField
                            sx={{ width: "50%" }}
                            fullWidth
                            name="roomDetails.squareMeters"
                            placeholder="Area of room in sqm"
                            label="Area (sqm)"
                        />
                    </LocalizedStrict>
                    <LocalizedStrict id="resources-create-room-tab-general-color-code-label" attrs={{ label: true }}>
                        <FormikDropdown sx={{ width: "50%" }} name="color" label="Color code">
                            <MenuItem value={RoomDetailsColor.RED}>
                                <LocalizedStrict id="resources-create-room-tab-general-color-red">
                                    <span>Red</span>
                                </LocalizedStrict>
                            </MenuItem>
                            <MenuItem value={RoomDetailsColor.GREEN}>
                                <LocalizedStrict id="resources-create-room-tab-general-color-green">
                                    <span>Green</span>
                                </LocalizedStrict>
                            </MenuItem>
                            <MenuItem value={RoomDetailsColor.BLUE}>
                                <LocalizedStrict id="resources-create-room-tab-general-color-blue">
                                    <span>Blue</span>
                                </LocalizedStrict>
                            </MenuItem>
                            <MenuItem value={RoomDetailsColor.BLACK}>
                                <LocalizedStrict id="resources-create-room-tab-general-color-black">
                                    <span>Black</span>
                                </LocalizedStrict>
                            </MenuItem>
                        </FormikDropdown>
                    </LocalizedStrict>
                    <AVEquipmentSelector />
                    <LocalizedStrict id="resources-create-room-tab-general-tags-label" attrs={{ label: true }}>
                        <FormikAutoComplete
                            name="tags"
                            label="Tags"
                            freeSolo
                            multiple
                            options={values.tags ?? []}
                            fullWidth
                            renderInput={() => <TextField />}
                        />
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="resources-create-room-tab-general-tags-info-description"
                        attrs={{ title: true }}
                    >
                        <StandardAlertInfo
                            title={`Create one or more tags to be used for filtering resources. For example "External meeting room", "Guest meeting room", "Conference room", "Call room" etc.`}
                        />
                    </LocalizedStrict>
                </StandardMainPanel>
            </LocalizedStrict>

            <LocalizedStrict id="resources-create-room-tab-general-side-integrations" attrs={{ title: true }}>
                <StandardSidePanel variant="collapsible" title="Calendar integration">
                    <LocalizedStrict id="tab-availability-add-reminder-dialog-field-type-label" attrs={{ label: true }}>
                        <FormikDropdown name="integrationType" label="Type" fullWidth>
                            {[...iterateStringEnum(IntegrationType)].map((type) => (
                                <MenuItem key={type} value={type}>
                                    <LocalizedEnum base="integration-type-option" enumValue={type}>
                                        <span>{type}</span>
                                    </LocalizedEnum>
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>
                    {values.integrationType !== IntegrationType.NONE && (
                        <>
                            <LocalizedStrict id={integrationInfo.id} attrs={{ description: true }}>
                                <InfoBox description={integrationInfo.value} />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="resources-create-room-tab-general-email-address-label"
                                attrs={{ label: true }}
                            >
                                <FormikTextField fullWidth name="integrationEmailAddress" label="Email address" />
                            </LocalizedStrict>
                        </>
                    )}
                </StandardSidePanel>
            </LocalizedStrict>
            <StandardMainPanel title="Reminders">
                <NotificationsTable />
            </StandardMainPanel>
        </StandardForm>
    )
}
